import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Plus, ChevronDown, ChevronUp, Send, Trash2, Clock, Pencil,
  AlertCircle, CheckCircle, XCircle, ArrowRight, Calendar
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const statusStyles = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

const priorityStyles = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-orange-100 text-orange-700',
  high: 'bg-red-100 text-red-700',
};

// Class dasar buat native <select>, disamain sama Input/Textarea di form Timesheet
// (border-slate-200, rounded-xl, focus ring warna primary #1A3C34)
const selectBaseClass =
  'flex h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]';

export default function TaskManagement() {
  const user = getUser();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '', description: '', assigned_to: '', priority: 'medium', due_date: ''
  });
  const [formData, setFormData] = useState({
    title: '', description: '', assigned_to: '', priority: 'medium', due_date: ''
  });

  const canCreateTask = user?.role === 'admin' || user?.has_subordinates;

  useEffect(() => {
    fetchTasks();
    if (canCreateTask) fetchAssignees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTasks = async () => {
    try {
      const res = await api.get('/tasks');
      setTasks(res.data);
    } catch (err) {
      toast.error('Gagal memuat tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignees = async () => {
    try {
      if (user?.role === 'admin') {
        const res = await api.get('/users/employees');
        setEmployees(res.data);
      } else {
        const res = await api.get('/users/subordinates');
        setEmployees(res.data);
      }
    } catch (err) {
      console.error('Failed to load assignees:', err);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tasks', {
        ...formData,
        due_date: formData.due_date || null,
      });
      toast.success('Task created!');
      setShowForm(false);
      setFormData({ title: '', description: '', assigned_to: '', priority: 'medium', due_date: '' });
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    const statusLabel = STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus;
    if (!window.confirm(`Yakin ingin mengubah status task ini menjadi "${statusLabel}"?`)) {
      return;
    }
    try {
      await api.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Status updated!');
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal update status');
    }
  };

  const handleAddNote = async (taskId) => {
    const draft = (noteDrafts[taskId] || '').trim();
    if (!draft) return;
    try {
      await api.post(`/tasks/${taskId}/notes`, { text: draft });
      toast.success('Note added!');
      setNoteDrafts((prev) => ({ ...prev, [taskId]: '' }));
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menambah note');
    }
  };

  const canEditTask = (task) => {
    return task.assigned_by === user?.id || user?.role === 'admin';
  };

  const handleEditTask = (task) => {
    if (!canEditTask(task)) {
      toast.error('Hanya pembuat atau admin yang bisa edit task');
      return;
    }
    setEditFormData({
      title: task.title,
      description: task.description || '',
      assigned_to: task.assigned_to,
      priority: task.priority,
      due_date: task.due_date || '',
    });
    setEditingTaskId(task.id);
    setExpandedTask(task.id);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/tasks/${editingTaskId}`, {
        title: editFormData.title,
        description: editFormData.description,
        priority: editFormData.priority,
        due_date: editFormData.due_date || null,
        assigned_to: editFormData.assigned_to,
      });
      toast.success('Task updated!');
      setEditingTaskId(null);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal update task');
    }
  };

  const cancelEdit = () => {
    setEditingTaskId(null);
    setEditFormData({ title: '', description: '', assigned_to: '', priority: 'medium', due_date: '' });
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success('Task deleted!');
      if (expandedTask === taskId) setExpandedTask(null);
      if (editingTaskId === taskId) setEditingTaskId(null);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menghapus task');
    }
  };

  const filtered = filterStatus === 'all' ? tasks : tasks.filter(t => t.status === filterStatus);

  const counts = {
    all: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    cancelled: tasks.filter(t => t.status === 'cancelled').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header — samain sama Timesheet.js */}
      <div className="pb-2 border-b border-slate-200/70 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="task-mgmt-title">
            Task Management
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {canCreateTask ? 'Assign and track tasks for your team' : 'View and update your assigned tasks'}
          </p>
        </div>
        {canCreateTask && (
          <Button
            onClick={() => setShowForm(!showForm)}
            data-testid="create-task-btn"
            className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
          >
            <Plus className="w-5 h-5 mr-2" />
            {showForm ? 'Cancel' : 'New Task'}
          </Button>
        )}
      </div>

      {showForm ? (
        /* Create Task Form — list disembunyikan selama form terbuka, sama kayak Timesheet.js */
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">Create New Task</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Isi detail task yang mau di-assign</p>
          </div>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="title" className="text-sm font-medium text-slate-700">Title *</Label>
                <Input
                  id="title"
                  data-testid="task-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Task title"
                  className="w-full border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description" className="text-sm font-medium text-slate-700">Description</Label>
                <Textarea
                  id="description"
                  data-testid="task-desc-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Task description..."
                  rows={3}
                  className="border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="assignee" className="text-sm font-medium text-slate-700">Assign To *</Label>
                <select
                  id="assignee"
                  data-testid="task-assignee-select"
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  required
                  className={selectBaseClass}
                >
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority" className="text-sm font-medium text-slate-700">Priority</Label>
                <select
                  id="priority"
                  data-testid="task-priority-select"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className={selectBaseClass}
                >
                  {PRIORITY_OPTIONS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="dueDate" className="text-sm font-medium text-slate-700">Due Date (Optional)</Label>
                <div className="relative">
                  <Input
                    id="dueDate"
                    type="date"
                    data-testid="task-duedate-input"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full border-slate-200 rounded-xl text-sm pr-10 focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
                  />
                  <Calendar className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                data-testid="submit-task-btn"
                className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
              >
                Create Task
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
              >
                Batal
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <>
          {/* Status Filters */}
          <div className="flex flex-wrap gap-2" data-testid="task-filters">
            {[
              { key: 'all', label: 'All' },
              { key: 'pending', label: 'Pending' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'completed', label: 'Completed' },
              { key: 'cancelled', label: 'Cancelled' },
            ].map(f => (
              <Button
                key={f.key}
                size="sm"
                onClick={() => setFilterStatus(f.key)}
                data-testid={`filter-${f.key}`}
                className={
                  filterStatus === f.key
                    ? 'bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]'
                    : 'rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800 bg-white border'
                }
              >
                {f.label} ({counts[f.key]})
              </Button>
            ))}
          </div>

          {/* Task List */}
          {filtered.length === 0 ? (
            <Card className="p-12 bg-white border-slate-200/80 shadow-sm">
              <div className="text-center space-y-4">
                <div className="inline-flex p-6 rounded-full bg-slate-100">
                  <Clock className="w-12 h-12 text-slate-400" />
                </div>
                <h3 className="text-xl font-bold font-heading text-slate-900">No Tasks</h3>
                <p className="text-muted-foreground text-sm">
                  {canCreateTask ? 'Create a task to get started' : 'No tasks assigned to you yet'}
                </p>
              </div>
            </Card>
          ) : (
            <div className="space-y-4">
              {filtered.map((task) => {
                const isExpanded = expandedTask === task.id;
                const isCreator = task.assigned_by === user?.id;
                const isAssignee = task.assigned_to === user?.id;
                const isAdmin = user?.role === 'admin';
                const canDelete = isCreator || isAdmin;

                return (
                  <Card
                    key={task.id}
                    className="overflow-hidden bg-white border-slate-200/80 shadow-sm rounded-2xl"
                    data-testid={`task-card-${task.id}`}
                  >
                    {/* Task Header */}
                    <div
                      className="p-5 cursor-pointer hover:bg-[#1A3C34]/5 transition-colors"
                      onClick={() => setExpandedTask(isExpanded ? null : task.id)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-bold font-heading text-slate-900 truncate">{task.title}</h3>
                            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${statusStyles[task.status]}`}>
                              {STATUS_OPTIONS.find(s => s.value === task.status)?.label}
                            </span>
                            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${priorityStyles[task.priority]}`}>
                              {task.priority.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                            <span>Assigned to: <strong className="text-gray-600">{task.assigned_to_name}</strong></span>
                            <span>By: <strong className="text-gray-600">{task.assigned_by_name}</strong></span>
                            {task.due_date && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                Due: {new Date(task.due_date).toLocaleDateString('id-ID')}
                              </span>
                            )}
                            {task.notes.length > 0 && (
                              <span className="text-[#1A3C34] font-medium">{task.notes.length} note{task.notes.length > 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {canEditTask(task) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditTask(task); }}
                              className="p-1.5 rounded-md text-slate-400 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                              data-testid={`edit-task-${task.id}`}
                              title="Edit Task"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                              className="p-1.5 rounded-md text-slate-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                              data-testid={`delete-task-${task.id}`}
                              title="Delete Task"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="border-t border-slate-100 p-5 space-y-5">
                        {/* Edit Form — creator/admin only */}
                        {editingTaskId === task.id && canEditTask(task) && (
                          <form onSubmit={handleUpdateTask} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-4 bg-slate-50 border-b border-slate-200">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-[#1A3C34] text-white flex items-center justify-center">
                                  <Pencil className="w-4 h-4" />
                                </div>
                                <div>
                                  <h4 className="text-sm font-bold text-slate-900">Edit Task</h4>
                                  <p className="text-xs text-muted-foreground">Perbarui detail task sebelum menyimpan</p>
                                </div>
                              </div>
                              <button type="button" onClick={cancelEdit} className="w-8 h-8 rounded-xl hover:bg-white border border-transparent hover:border-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors">
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="p-5 space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2 md:col-span-2">
                                  <Label className="text-sm font-medium text-slate-700">Title *</Label>
                                  <Input value={editFormData.title} onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })} required placeholder="Task title" className="bg-white border-slate-200 rounded-xl text-sm placeholder:text-slate-300 focus-visible:ring-[#1A3C34]" />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                  <Label className="text-sm font-medium text-slate-700">Description</Label>
                                  <Textarea value={editFormData.description} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} rows={3} placeholder="Task description..." className="bg-white border-slate-200 rounded-xl text-sm placeholder:text-slate-300 focus-visible:ring-[#1A3C34]" />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-slate-700">Assign To *</Label>
                                  <select value={editFormData.assigned_to} onChange={(e) => setEditFormData({ ...editFormData, assigned_to: e.target.value })} required className={selectBaseClass + ' bg-white'}>
                                    <option value="" disabled>Select Employee</option>
                                    {employees.map((emp) => (
                                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-slate-700">Priority</Label>
                                  <select value={editFormData.priority} onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })} className={selectBaseClass + ' bg-white'}>
                                    {PRIORITY_OPTIONS.map(p => (<option key={p.value} value={p.value}>{p.label}</option>))}
                                  </select>
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                  <Label className="text-sm font-medium text-slate-700">Due Date</Label>
                                  <div className="relative max-w-xs">
                                    <Input type="date" value={editFormData.due_date} onChange={(e) => setEditFormData({ ...editFormData, due_date: e.target.value })} className="bg-white border-slate-200 rounded-xl text-sm pr-10 focus-visible:ring-[#1A3C34] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0" />
                                    <Calendar className="w-4 h-4 text-slate-400 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
                              <Button type="button" variant="outline" onClick={cancelEdit} className="rounded-xl border-slate-200 bg-white">Batal</Button>
                              <Button type="submit" className="bg-[#1A3C34] hover:bg-[#142E27] text-white rounded-xl shadow-md">Simpan Perubahan</Button>
                            </div>
                          </form>
                        )}

                        {/* Description — hide when editing */}
                        {editingTaskId !== task.id && task.description && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-1">Description</p>
                            <p className="text-sm text-slate-700">{task.description}</p>
                          </div>
                        )}

                        {/* Status Update */}
                        {(isAssignee || isCreator || isAdmin) && task.status !== 'completed' && task.status !== 'cancelled' && (
                          <div>
                            <p className="text-sm text-muted-foreground mb-2">Update Status</p>
                            <div className="flex flex-wrap gap-2">
                              {STATUS_OPTIONS.filter(s => s.value !== task.status).map(s => (
                                <Button
                                  key={s.value}
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleStatusChange(task.id, s.value)}
                                  data-testid={`status-${s.value}-${task.id}`}
                                  className="gap-1.5 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                                >
                                  {s.value === 'in_progress' && <ArrowRight className="w-3.5 h-3.5" />}
                                  {s.value === 'completed' && <CheckCircle className="w-3.5 h-3.5" />}
                                  {s.value === 'cancelled' && <XCircle className="w-3.5 h-3.5" />}
                                  {s.value === 'pending' && <AlertCircle className="w-3.5 h-3.5" />}
                                  {s.label}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        <div>
                          <p className="text-sm text-muted-foreground mb-2">Notes ({task.notes.length})</p>
                          {task.notes.length > 0 && (
                            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                              {task.notes.map((note, idx) => (
                                <div key={idx} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-semibold text-slate-800">{note.author_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(note.created_at).toLocaleString('id-ID')}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-700">{note.text}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {(isAssignee || isCreator || isAdmin) && (
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add a note..."
                                value={noteDrafts[task.id] || ''}
                                onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [task.id]: e.target.value }))}
                                data-testid={`note-input-${task.id}`}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNote(task.id); } }}
                                className="border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleAddNote(task.id)}
                                data-testid={`add-note-btn-${task.id}`}
                                className="shrink-0 bg-[#1A3C34] hover:bg-[#142E27] text-white rounded-xl"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Meta */}
                        <div className="text-xs text-muted-foreground pt-2 border-t border-slate-100">
                          Created: {new Date(task.created_at).toLocaleString('id-ID')} | Updated: {new Date(task.updated_at).toLocaleString('id-ID')}
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}